// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import produce from 'immer';
import type { HistoricalBalances } from '@shared/data-access/types/brokerage-types';

const EMPTY_HISTORICAL_BALANCES: HistoricalBalances = {
  cash: [],
  debt: [],
  investment: [],
  net_value: [],
  dates: [],
};

/**
 * Returns all unique dates from multiple balance series, sorted chronologically.
 * Pure function: no side effects, deterministic.
 */
function getUniqueSortedDates(balances: HistoricalBalances[]): string[] {
  const set = balances.reduce<Set<string>>(
    (acc, b) => b.dates.reduce((s, d) => s.add(d), acc),
    new Set(),
  );
  return Array.from(set).sort();
}

/**
 * Returns the numeric value at a given date in a series, or 0 if missing/invalid.
 * Pure function: no side effects, deterministic.
 */
function getValueAtDate(series: number[], dates: string[], date: string): number {
  const idx = dates.indexOf(date);
  if (idx === -1) return 0;
  const v = series[idx];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** Result of summing all series at a single date (scalars, not arrays). */
type SumAtDateResult = {
  cash: number;
  debt: number;
  investment: number;
  net_value: number;
};

/**
 * Sums cash, debt, investment and net_value across all balance series at a single
 * date. Uses Immer to mutate a draft immutably. Pure from the outside:
 * deterministic.
 */
function sumAtDate(balances: HistoricalBalances[], date: string): SumAtDateResult {
  return balances.reduce(
    (acc, b) =>
      produce(acc, draft => {
        draft.cash += getValueAtDate(b.cash, b.dates, date);
        draft.debt += getValueAtDate(b.debt, b.dates, date);
        draft.investment += getValueAtDate(b.investment, b.dates, date);
        draft.net_value += getValueAtDate(b.net_value, b.dates, date);
      }),
    { cash: 0, debt: 0, investment: 0, net_value: 0 },
  );
}

/**
 * Aggregates multiple historical balance series into a single timeline (window).
 * Returns one HistoricalBalances object where dates, cash, debt, investment and
 * net_value all have the same length. For each date in the window, values are
 * summed from every series that has data for that date. Uses Immer to build the
 * result immutably.
 */
export function aggregateHistoricalBalances(
  balances: HistoricalBalances[],
): HistoricalBalances {
  if (balances.length === 0) return EMPTY_HISTORICAL_BALANCES;

  const dates = getUniqueSortedDates(balances);
  if (dates.length === 0) return EMPTY_HISTORICAL_BALANCES;

  return produce(EMPTY_HISTORICAL_BALANCES, draft => {
    for (const date of dates) {
      const s = sumAtDate(balances, date);
      draft.dates.push(date);
      draft.cash.push(s.cash);
      draft.debt.push(s.debt);
      draft.investment.push(s.investment);
      draft.net_value.push(s.net_value);
    }
  });
}
